<template>
  <div class="mt-page">
    <!-- Loading state -->
    <div v-if="isCurriculumLoading" class="flex items-center justify-center py-20">
      <MTIcon name="loader" :size="32" class="animate-spin" :style="{ color: 'var(--mt-accent-blue)' }" />
    </div>

    <template v-else>
      <!-- WELCOME HEADER -->
      <div class="welcome-header mb-5">
        <div class="flex flex-wrap items-center gap-4">
          <!-- Avatar + Greeting -->
          <div class="flex items-center gap-3">
            <MTAvatar :studentId="childId" size="lg" />
            <div>
              <h1 class="text-xl md:text-2xl font-extrabold" :style="{ color: 'var(--mt-text)' }">
                Hi, {{ studentName }}!
              </h1>
              <p class="text-sm" :style="{ color: 'var(--mt-text-muted)' }">Ready to learn today?</p>
            </div>
          </div>

          <!-- Streak -->
          <div class="streak-pill">
            <span class="flame-wobble">🔥</span>
            <span class="streak-count">{{ streak }}</span>
            <span class="streak-label">day streak</span>
            <span v-if="activeToday" class="active-badge">Active today</span>
          </div>

          <!-- XP Meter -->
          <div class="flex items-center gap-3 ml-auto">
            <div class="level-badge">
              <span class="text-white font-extrabold text-sm">{{ xp.level }}</span>
            </div>
            <div class="w-36">
              <div class="flex justify-between text-xs font-bold mb-1">
                <span :style="{ color: 'var(--mt-accent-purple)' }">Level {{ xp.level }}</span>
                <span :style="{ color: 'var(--mt-text-muted)' }">{{ xp.total }} / {{ xp.nextThreshold }}</span>
              </div>
              <div class="h-3 rounded-full overflow-hidden" :style="{ background: 'var(--mt-track-bg)' }">
                <div
                  class="xp-bar h-full rounded-full"
                  :style="{ width: Math.round(xp.progressToNext * 100) + '%' }"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- MAIN 2-COLUMN: Mission Pad + Optional Tasks -->
      <div class="grid grid-cols-1 lg:grid-cols-5 gap-5 mb-5">

        <!-- LEFT: MY MISSION PAD -->
        <div class="lg:col-span-3 lg:h-80">
          <div class="mission-pad h-full overflow-y-auto">
            <!-- Punch holes -->
            <div class="punch-holes">
              <div v-for="i in 3" :key="'hole-'+i" class="punch-hole"></div>
            </div>
            <div class="pad-margin-line"></div>

            <!-- Header -->
            <div class="pad-header">
              <div class="flex items-center gap-2">
                <MTIcon name="clipboard-list" :size="22" class="pad-icon" />
                <h2 class="pad-title">My Mission Pad</h2>
              </div>
              <span class="done-badge" :class="allDone ? 'done-badge-complete' : ''">
                {{ completedCount }}/{{ taskEngine.tasks.value.length }} done
                <template v-if="allDone"> ⭐</template>
              </span>
            </div>

            <!-- Celebration banner (when all done) — sits above tasks so they stay visible -->
            <div v-if="allDone" class="celebration-banner">
              <div class="celebration-inner">
                <span class="celebration-emoji">🎉</span>
                <p class="celebration-title">All missions complete!</p>
                <p class="celebration-subtitle">
                  You earned <span class="celebration-xp">{{ todayXPEarned }} XP</span> today
                </p>
              </div>
            </div>

            <!-- Task List -->
            <div class="pad-tasks" :style="{ '--pad-rows': taskEngine.tasks.value.length }">
              <!-- Writing pen -->
              <div
                v-if="taskEngine.tasks.value.length && !padAnimDone"
                class="pad-pen"
                :style="{ animationDuration: (taskEngine.tasks.value.length * 0.8 + 0.5) + 's' }"
              >
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <!-- Pen body -->
                  <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" fill="#f59e0b" stroke="#d97706" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <!-- Pen tip -->
                  <path d="M2 22l1.5-5.5L7.5 20.5Z" fill="#374151"/>
                  <!-- Pen highlight -->
                  <path d="m15 5 4 4" stroke="#fbbf24" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  <!-- Shine -->
                  <path d="M18 4.5l1 1" stroke="white" stroke-width="0.8" stroke-linecap="round" opacity="0.6"/>
                </svg>
              </div>
              <div
                v-for="(task, idx) in taskEngine.tasks.value"
                :key="task.type + (task.lessonId || '')"
                class="pad-task-row"
                :class="[{
                  'pad-task-done': task.completed,
                  'pad-task-pending': !task.completed
                }, padAnimDone ? '' : 'pad-task-write']"
                :style="padAnimDone ? {} : { animationDelay: (idx * 0.8) + 's' }"
                @click="!task.completed && handleTaskStart(task)"
              >
                <!-- Icon / Checkbox -->
                <div v-if="task.completed" class="pad-checkbox pad-checkbox-checked">
                  <MTIcon name="check" :size="18" class="text-white" />
                </div>
                <div v-else class="pad-checkbox pad-task-icon" :class="taskIconBgClass(task.type)">
                  <MTIcon :name="taskIconName(task.type)" :size="16" class="text-white" />
                </div>

                <!-- Content -->
                <div class="flex-1 min-w-0">
                  <p class="pad-task-title" :class="{ 'line-through': task.completed }">
                    {{ task.title }}
                  </p>
                  <p class="pad-task-subtitle">{{ task.subtitle }}</p>
                </div>

                <!-- XP + Action -->
                <template v-if="!task.completed">
                  <span class="xp-pill">+{{ task.xp }} XP</span>
                  <button
                    class="go-button"
                    :class="goButtonClass(task.type)"
                    @click.stop="handleTaskStart(task)"
                  >
                    Go <MTIcon name="arrow-right" :size="14" />
                  </button>
                </template>
                <span v-else class="done-label">Done</span>
              </div>

              <!-- No tasks at all -->
              <div v-if="!taskEngine.tasks.value.length" class="pad-task-row justify-center">
                <p class="text-sm" :style="{ color: 'var(--mt-text-muted)' }">No tasks for today</p>
              </div>
            </div>

            <div class="h-4"></div>
          </div>
        </div>

        <!-- RIGHT: WANT TO DO MORE? -->
        <div class="lg:col-span-2 lg:h-80">
          <div class="optional-card h-full overflow-y-auto">
            <div class="flex items-center gap-2 mb-4">
              <div class="optional-icon-box">
                <MTIcon name="rocket" :size="18" class="text-white" />
              </div>
              <h2 class="text-lg font-extrabold" :style="{ color: 'var(--mt-text)' }">
                {{ isSpaceTheme ? 'Side Missions' : 'Want to Do More?' }}
              </h2>
            </div>

            <div class="space-y-3">
              <!-- Revision (Pro/Family only, only when topics have been practiced) -->
              <div
                v-if="!hasRevisionTask && revisionEnabled && hasPracticedTopics"
                class="optional-item optional-item-purple"
                @click="startMission('revision')"
              >
                <div class="optional-accent optional-accent-purple"></div>
                <div class="optional-item-icon optional-item-icon-purple">
                  <MTIcon name="brain" :size="18" />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="optional-item-title">Quick Revision</p>
                  <p class="optional-item-desc">Review past topics</p>
                </div>
                <button class="optional-btn optional-btn-purple">Start</button>
              </div>

              <!-- Next Lesson -->
              <div
                class="optional-item optional-item-blue"
                @click="startMission('lesson')"
              >
                <div class="optional-accent optional-accent-blue"></div>
                <div class="optional-item-icon optional-item-icon-blue">
                  <MTIcon name="book-open" :size="18" />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="optional-item-title">{{ nextLessonTitle }}</p>
                  <p class="optional-item-desc">Next lesson to explore</p>
                </div>
                <button class="optional-btn optional-btn-blue">Start</button>
              </div>

              <!-- Skills Check (limited by plan) -->
              <div
                v-if="!hasDiagnosticTask && skillsCheckAllowed"
                class="optional-item optional-item-orange"
                @click="startMission('diagnostic')"
              >
                <div class="optional-accent optional-accent-orange"></div>
                <div class="optional-item-icon optional-item-icon-orange">
                  <MTIcon name="flask-conical" :size="18" />
                </div>
                <div class="flex-1 min-w-0">
                  <p class="optional-item-title">Skills Check</p>
                  <p class="optional-item-desc">See how you're doing!</p>
                </div>
                <button class="optional-btn optional-btn-orange">Start</button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- JOURNEY SNAPSHOT -->
      <RouterLink :to="`/student/${childId}/curriculum`" class="block mb-5 group">
        <div class="journey-card">
          <div class="flex items-center justify-between mb-3">
            <h2 class="text-lg font-extrabold flex items-center gap-2" :style="{ color: 'var(--mt-text)' }">
              <MTIcon name="map" :size="18" :style="{ color: 'var(--mt-accent-blue)' }" />
              {{ isSpaceTheme ? 'Your Star Map' : 'Your Learning Journey' }}
            </h2>
            <span class="text-xs font-bold group-hover:underline flex items-center gap-1" :style="{ color: 'var(--mt-accent-blue)' }">
              {{ isSpaceTheme ? 'See full map' : 'See full journey' }}
              <MTIcon name="arrow-right" :size="14" />
            </span>
          </div>
          <JourneyMiniPreview
            v-if="journeyUnits.length"
            :units="journeyUnits"
            :theme="preferencesStore.homeTheme"
            @select="navigateToLesson"
          />
          <p v-else class="text-sm" :style="{ color: 'var(--mt-text-muted)' }">No curriculum loaded yet.</p>
        </div>
      </RouterLink>
    </template>
  </div>

  <!-- Upgrade toast -->
  <Transition name="sh-toast">
    <div v-if="upgradeToast" class="sh-upgrade-toast">
      <MTIcon name="lock" :size="18" class="flex-shrink-0" />
      <span class="flex-1">{{ upgradeToast }}</span>
      <RouterLink to="/parent/subscription" class="sh-toast-btn">Upgrade</RouterLink>
    </div>
  </Transition>
</template>

<script setup>
import { computed, onMounted, ref, watch } from "vue";
import { useRouter, useRoute } from "vue-router";

import MTIcon from "@/components/ui/MTIcon.vue";
import MTAvatar from "@/components/ui/MTAvatar.vue";
import JourneyMiniPreview from "@/components/learning/JourneyMiniPreview.vue";

import { useStudentStore } from "@/stores/studentStore";
import { useProgressStore } from "@/stores/progressStore";
import { useCurriculumStore } from "@/stores/curriculumStore";
import { usePreferencesStore } from "@/stores/preferencesStore";

import { useDynamicTaskEngine } from "@/composables/useDynamicTaskEngine";
import { useStreakEngine } from "@/composables/useStreakEngine";
import { useContinueLearningEngine } from "@/composables/useContinueLearningEngine";
import { useJourneyUnits } from "@/composables/useJourneyUnits";
import { usePlanLimits } from "@/composables/usePlanLimits";

const router = useRouter();
const route = useRoute();
const studentStore = useStudentStore();
const progressStore = useProgressStore();
const curriculumStore = useCurriculumStore();
const preferencesStore = usePreferencesStore();

const isCurriculumLoading = ref(true);
const upgradeToast = ref(null);
let upgradeToastTimer = null;

const childId = computed(() => route.params.studentId || studentStore.activeStudent?.id);

// Student info
const student = computed(() => studentStore.students.find(s => s.id === childId.value));
const studentName = computed(() => student.value?.name || "Student");

// Theme
const isSpaceTheme = computed(() => preferencesStore.homeTheme === 'space-mission');

// Engines
const taskEngine = useDynamicTaskEngine(childId.value);
const streakEngine = useStreakEngine(childId.value);
const streak = computed(() => streakEngine.streak.value);
const activeToday = computed(() => streakEngine.activeToday?.value ?? false);
const engine = useContinueLearningEngine(childId.value);

// XP
const xp = computed(() => progressStore.getXP(childId.value));

// Journey units
const { journeyUnits, flatLessons, currentLessonIndex } = useJourneyUnits(childId.value);

// Next lesson for optional "Want to Do More?" section
// If mission pad already shows the current lesson as an active task, skip to the one after it.
// Then skip any already-completed (secure+) lessons to find one that still needs work.
const sideMissionLessonIndex = computed(() => {
  const idx = currentLessonIndex.value;
  if (idx === -1) return -1;
  const hasActiveLessonTask = taskEngine.tasks.value.some(
    t => (t.type === "continue" || t.type === "newLesson") && !t.completed
  );
  let startIdx = hasActiveLessonTask ? idx + 1 : idx;
  // Walk forward past any lessons where all topics are already at secure+
  while (startIdx < flatLessons.value.length && flatLessons.value[startIdx]?.status === 'completed') {
    startIdx++;
  }
  return startIdx < flatLessons.value.length ? startIdx : -1;
});

const nextLessonTitle = computed(() => {
  const idx = sideMissionLessonIndex.value;
  if (idx === -1 || idx >= flatLessons.value.length) return "Next Lesson";
  const journey = flatLessons.value[idx];
  if (!journey) return "Next Lesson";
  const lesson = curriculumStore.allLessons.find(l => l.id === journey.id);
  return lesson?.title || "Next Lesson";
});

// Plan limits
const planLimits = usePlanLimits();
planLimits.loadPlans();

// "Want to do more?" — hide items already present in the mission pad
const hasRevisionTask = computed(() =>
  taskEngine.tasks.value.some(t => t.type === 'revision')
);
const hasDiagnosticTask = computed(() =>
  taskEngine.tasks.value.some(t => t.type === 'diagnostic')
);

// Feature gates
const revisionEnabled = computed(() => planLimits.isFeatureEnabled('revisionMode'));
const skillsCheckAllowed = computed(() =>
  planLimits.isFeatureEnabled('skillsCheck') && planLimits.canStartSkillsCheck(childId.value)
);
const hasPracticedTopics = computed(() => {
  const prog = progressStore.progress?.[childId.value] || {};
  return Object.keys(prog).some(k => k);
});

// Pen-writing animation state
const padAnimDone = ref(false);
watch(() => taskEngine.tasks.value.length, (len) => {
  if (len > 0 && !padAnimDone.value) {
    setTimeout(() => { padAnimDone.value = true; }, len * 800 + 1000);
  }
}, { immediate: true });

// Task helpers
const completedCount = computed(() => taskEngine.tasks.value.filter(t => t.completed).length);
const allDone = computed(() =>
  taskEngine.tasks.value.length > 0 && taskEngine.tasks.value.every(t => t.completed)
);


// XP earned today (sum of completed task XPs)
const todayXPEarned = computed(() =>
  taskEngine.tasks.value.filter(t => t.completed).reduce((sum, t) => sum + t.xp, 0)
);

onMounted(async () => {
  progressStore.ensureWeekly(childId.value);
  await curriculumStore.loadTrack("eplus_core");
  isCurriculumLoading.value = false;
});

// Navigation
function handleTaskStart(task) {
  router.push(task.route);
}

function navigateToLesson(lesson) {
  if (lesson.status === 'locked') {
    const msg = lesson.lockMessage || 'Upgrade your plan to unlock this lesson.';
    showUpgradeToast(msg);
    return;
  }
  router.push(`/student/${childId.value}/learn/${lesson.id}`);
}

function showUpgradeToast(msg) {
  upgradeToast.value = msg;
  clearTimeout(upgradeToastTimer);
  upgradeToastTimer = setTimeout(() => { upgradeToast.value = null; }, 4000);
}

function taskIconName(type) {
  return {
    continue: "book-open",
    newLesson: "book-open",
    revision: "brain",
    diagnostic: "flask-conical"
  }[type] || "target";
}

function taskIconBgClass(type) {
  return {
    continue: "pad-icon-blue",
    newLesson: "pad-icon-blue",
    revision: "pad-icon-purple",
    diagnostic: "pad-icon-amber"
  }[type] || "pad-icon-blue";
}

function goButtonClass(type) {
  const map = {
    continue: "go-button-blue",
    newLesson: "go-button-blue",
    revision: "go-button-purple",
    diagnostic: "go-button-amber"
  };
  return map[type] || "go-button-blue";
}

function startMission(type) {
  const sideLessonJourney = flatLessons.value[sideMissionLessonIndex.value];
  const sideLessonId = sideLessonJourney?.id || engine.suggestedNextLesson.value?.lessonId || "";
  const routes = {
    lesson: `/student/${childId.value}/learn/${sideLessonId}`,
    revision: `/student/${childId.value}/revision`,
    diagnostic: `/student/${childId.value}/skills-check`
  };
  router.push(routes[type]);
}
</script>

<style scoped>
@reference "tailwindcss";

/* ── Welcome Header ── */
.welcome-header {
  background: var(--mt-card-bg);
  border: 1px solid var(--mt-border);
  border-radius: 1rem;
  padding: 1rem 1.25rem;
  box-shadow: var(--mt-shadow);
}

.streak-pill {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: var(--mt-bg-subtle);
  border: 1px solid var(--mt-border);
  border-radius: 9999px;
  padding: 0.5rem 1rem;
}
.streak-count {
  font-weight: 800;
  color: #ea580c;
}
.streak-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--mt-text-muted);
}
.active-badge {
  font-size: 0.75rem;
  font-weight: 700;
  color: #15803d;
  background: #dcfce7;
  padding: 0.125rem 0.5rem;
  border-radius: 9999px;
}

.flame-wobble {
  display: inline-block;
  animation: flameWobble 1.5s ease-in-out infinite;
}
@keyframes flameWobble {
  0%, 100% { transform: rotate(-3deg) scale(1); }
  50% { transform: rotate(3deg) scale(1.1); }
}

.level-badge {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  background: linear-gradient(135deg, var(--mt-accent-purple), #6d28d9);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(124, 58, 237, 0.25);
}

.xp-bar {
  background: linear-gradient(90deg, var(--mt-accent-purple), #6d28d9);
  position: relative;
  overflow: hidden;
}
.xp-bar::after {
  content: '';
  position: absolute;
  top: 0; left: -100%;
  width: 60%; height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
  animation: shimmer 3s ease-in-out infinite;
}
@keyframes shimmer {
  0% { left: -100%; }
  100% { left: 200%; }
}

/* ── Mission Pad ── */
.mission-pad {
  background: var(--mt-card-bg);
  border: 1px solid var(--mt-border);
  border-radius: 1rem;
  box-shadow: var(--mt-shadow);
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  /* Ruled lines */
  background-image: repeating-linear-gradient(
    transparent, transparent 39px,
    var(--mt-border) 39px, var(--mt-border) 40px
  );
  background-position: 0 12px;
}

/* Punch holes */
.punch-holes {
  position: absolute;
  left: 12px;
  top: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  z-index: 3;
  padding: 2rem 0;
}
.punch-hole {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--mt-bg);
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.15), inset 0 0 0 1px rgba(0,0,0,0.06);
}

.pad-margin-line {
  position: absolute;
  left: 42px;
  top: 0;
  bottom: 0;
  width: 0;
  border-left: 2px solid #ef9a9a;
  opacity: 0.35;
  z-index: 1;
}

.pad-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem 0.75rem 4.5rem;
  position: relative;
  z-index: 2;
}
.pad-icon {
  color: var(--mt-accent-blue);
}
.pad-title {
  font-size: 1.25rem;
  font-weight: 800;
  color: var(--mt-text);
}

.done-badge {
  font-size: 0.8rem;
  font-weight: 800;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  background: #dcfce7;
  color: #15803d;
}
.done-badge-complete {
  background: #22c55e;
  color: white;
}

/* ── Pen Writing Animation ── */
.pad-pen {
  position: absolute;
  left: 4rem;
  top: 0.5rem;
  z-index: 10;
  color: var(--mt-text-muted);
  opacity: 0;
  transform: rotate(-30deg);
  animation: penTravel ease-in-out forwards;
  pointer-events: none;
}
@keyframes penTravel {
  0%   { left: 4rem; opacity: 0; }
  5%   { opacity: 0.8; }
  95%  { opacity: 0.7; }
  100% { left: 75%; opacity: 0; }
}

.pad-task-write {
  opacity: 0;
  transform: translateX(4px);
  animation: taskWrite 0.7s ease-out forwards;
}
@keyframes taskWrite {
  0%   { opacity: 0; transform: translateX(4px); clip-path: inset(0 100% 0 0); }
  50%  { clip-path: inset(0 30% 0 0); }
  100% { opacity: 1; transform: translateX(0); clip-path: inset(0 0 0 0); }
}

/* ── Task Rows ── */
.pad-tasks {
  position: relative;
  z-index: 2;
}

.pad-task-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.875rem 1.5rem 0.875rem 4.5rem;
  border-top: 1px solid var(--mt-border);
  transition: background 0.15s ease;
}
.pad-task-row:first-child {
  border-top: none;
}

.pad-task-done {
  opacity: 0.55;
}

.pad-task-pending {
  cursor: pointer;
  border-radius: 0.5rem;
  margin: 0.35rem 0.5rem 0.35rem 3.5rem;
  padding-left: 1.25rem;
  background: var(--mt-hover);
  transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease;
}
.pad-task-pending:hover {
  background: var(--mt-accent-blue-muted);
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
  transform: translateX(2px);
}

.pad-checkbox {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pad-checkbox-empty {
  border: 3px dashed var(--mt-border);
  background: var(--mt-bg-subtle);
}
.pad-task-icon {
  border: none;
}
.pad-icon-blue { background: #3b82f6; }
.pad-icon-purple { background: #7c3aed; }
.pad-icon-amber { background: #f59e0b; }

.pad-checkbox-checked {
  background: #22c55e;
  animation: checkPop 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
@keyframes checkPop {
  0% { transform: scale(0); }
  60% { transform: scale(1.3); }
  100% { transform: scale(1); }
}

.pad-task-title {
  font-weight: 700;
  font-size: 0.925rem;
  color: var(--mt-text);
}
.pad-task-done .pad-task-title {
  color: var(--mt-text-muted);
}
.pad-task-subtitle {
  font-size: 0.8rem;
  color: var(--mt-text-muted);
}

.xp-pill {
  font-size: 0.75rem;
  font-weight: 800;
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  background: var(--mt-accent-purple-muted);
  color: var(--mt-accent-purple);
  white-space: nowrap;
}

.go-button {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  font-size: 0.8rem;
  font-weight: 700;
  padding: 0.5rem 1rem;
  border-radius: 9999px;
  color: white;
  cursor: pointer;
  transition: all 0.15s ease;
  border: none;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}
.go-button:hover {
  transform: scale(1.05);
}
.go-button-blue { background: #3b82f6; }
.go-button-blue:hover { background: #2563eb; }
.go-button-purple { background: #7c3aed; }
.go-button-purple:hover { background: #6d28d9; }
.go-button-amber { background: #f59e0b; }
.go-button-amber:hover { background: #d97706; }

.done-label {
  font-size: 0.8rem;
  font-weight: 700;
  color: var(--mt-text-muted);
}

/* ── Celebration ── */
.celebration-banner {
  margin: 0.375rem 1rem 0;
  position: relative;
  z-index: 2;
}
.celebration-inner {
  background: linear-gradient(135deg, #22c55e, #14b8a6, #06b6d4);
  border-radius: 0.625rem;
  padding: 0.5rem 1rem;
  margin-left: 2.5rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
}
.celebration-emoji {
  font-size: 1.125rem;
  flex-shrink: 0;
}
.celebration-title {
  font-weight: 800;
  font-size: 0.875rem;
  color: white;
}
.celebration-subtitle {
  font-size: 0.75rem;
  font-weight: 600;
  color: rgba(255,255,255,0.85);
}
.celebration-xp {
  color: #fef08a;
  font-weight: 800;
}

/* ── Optional Tasks Card ── */
.optional-card {
  border: 2px dashed var(--mt-border);
  border-radius: 1rem;
  padding: 1.25rem;
  background: var(--mt-bg-subtle);
  height: 100%;
}

.optional-icon-box {
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  background: linear-gradient(135deg, #f97316, #ec4899);
  display: flex;
  align-items: center;
  justify-content: center;
}

.optional-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: var(--mt-card-bg);
  border-radius: 0.75rem;
  padding: 0.875rem;
  border: 1px solid var(--mt-border);
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}
.optional-item:hover {
  transform: translateY(-2px);
  box-shadow: var(--mt-shadow-lg);
}

.optional-accent {
  width: 3px;
  align-self: stretch;
  border-radius: 9999px;
  margin: -0.25rem 0 -0.25rem -0.25rem;
}
.optional-accent-purple { background: #7c3aed; }
.optional-accent-blue { background: #3b82f6; }
.optional-accent-orange { background: #f97316; }

.optional-item-icon {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 0.75rem;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.optional-item-icon-purple { background: var(--mt-icon-purple-bg); color: var(--mt-icon-purple-fg); }
.optional-item-icon-blue { background: var(--mt-icon-blue-bg); color: var(--mt-icon-blue-fg); }
.optional-item-icon-orange { background: #fff7ed; color: #ea580c; }

.optional-item-title {
  font-weight: 700;
  font-size: 0.875rem;
  color: var(--mt-text);
}
.optional-item-desc {
  font-size: 0.75rem;
  color: var(--mt-text-muted);
}

.optional-btn {
  font-size: 0.75rem;
  font-weight: 700;
  padding: 0.375rem 0.75rem;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: opacity 0.15s;
}
.optional-btn:hover { opacity: 0.85; }
.optional-btn-purple { background: var(--mt-accent-purple-muted); color: var(--mt-accent-purple); }
.optional-btn-blue { background: var(--mt-accent-blue-muted); color: var(--mt-accent-blue); }
.optional-btn-orange { background: #fff7ed; color: #ea580c; }

/* ── Journey Card ── */
.journey-card {
  background: var(--mt-card-bg);
  border: 1px solid var(--mt-border);
  border-radius: 1rem;
  padding: 1.25rem;
  box-shadow: var(--mt-shadow);
  transition: border-color 0.15s;
}
.journey-card:hover {
  border-color: var(--mt-accent-blue-muted);
}

/* ── Mobile Responsiveness ── */
@media (max-width: 640px) {
  .pad-header {
    padding-left: 1.25rem;
  }
  .pad-task-row {
    padding-left: 1.25rem;
    gap: 0.75rem;
  }
  .pad-task-active {
    padding-left: 1rem;
  }
  .pad-margin-line,
  .punch-holes {
    display: none;
  }
  .celebration-inner {
    margin-left: 0;
    gap: 0.5rem;
  }
  .streak-pill {
    order: 3;
    width: 100%;
    justify-content: center;
  }
}

/* ── Upgrade Toast ── */
.sh-upgrade-toast {
  position: fixed;
  bottom: 1.5rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  background: #1e293b;
  color: #f1f5f9;
  border: 1px solid #334155;
  border-radius: 0.875rem;
  padding: 0.75rem 1rem;
  box-shadow: 0 8px 30px rgba(0,0,0,0.35);
  z-index: 9999;
  min-width: 280px;
  max-width: 90vw;
  font-size: 0.875rem;
}
.sh-toast-btn {
  background: #f59e0b;
  color: #1c1917;
  font-weight: 700;
  font-size: 0.75rem;
  padding: 0.3rem 0.75rem;
  border-radius: 0.5rem;
  white-space: nowrap;
  text-decoration: none;
}
.sh-toast-btn:hover { background: #d97706; }

.sh-toast-enter-active, .sh-toast-leave-active { transition: all 0.25s ease; }
.sh-toast-enter-from, .sh-toast-leave-to { opacity: 0; transform: translateX(-50%) translateY(12px); }
</style>

<!-- Theme overrides must be in a NON-scoped block.
     :global() in <style scoped> strips descendant selectors during build,
     causing styles to apply to <html> instead of child elements. -->
<style>
/* ── Space Theme Overrides ── */
.theme-space .welcome-header {
  background: linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(30, 58, 138, 0.4));
  border-color: rgba(6, 182, 212, 0.3);
}

.theme-space .active-badge {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
  border: 1px solid rgba(34, 197, 94, 0.25);
}

.theme-space .streak-pill {
  background: rgba(15, 23, 42, 0.6);
  border-color: rgba(251, 146, 60, 0.2);
}
.theme-space .streak-count {
  color: #fb923c;
}

.theme-space .mission-pad {
  background-color: #1e293b;
  border: 1px solid rgba(6, 182, 212, 0.35);
  background-image: repeating-linear-gradient(
    transparent, transparent 39px,
    rgba(100, 200, 255, 0.12) 39px, rgba(100, 200, 255, 0.12) 40px
  );
}
.theme-space .pad-task-row {
  border-color: rgba(100, 200, 255, 0.12);
}

.theme-space .pad-margin-line {
  border-color: #06b6d4;
  opacity: 0.3;
}
.theme-space .punch-hole {
  background: #0f172a;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.4), inset 0 0 0 1px rgba(255,255,255,0.05);
}

.theme-space .pad-icon {
  color: #22d3ee;
}

.theme-space .pad-task-active {
  background: rgba(6, 182, 212, 0.08);
}

.theme-space .pad-checkbox-empty {
  border-color: #475569;
  background: #0f172a;
}

.theme-space .done-badge {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
  border: 1px solid rgba(34, 197, 94, 0.25);
}
.theme-space .done-badge-complete {
  background: #22c55e;
  color: white;
}

.theme-space .xp-pill {
  border: 1px solid rgba(167, 139, 250, 0.25);
}

.theme-space .go-button-blue {
  background: #06b6d4;
  box-shadow: 0 2px 12px rgba(6, 182, 212, 0.35);
}
.theme-space .go-button-purple {
  background: #8b5cf6;
  box-shadow: 0 2px 12px rgba(139, 92, 246, 0.35);
}
.theme-space .go-button-amber {
  background: #f59e0b;
  box-shadow: 0 2px 12px rgba(245, 158, 11, 0.35);
}

.theme-space .optional-card {
  background: rgba(15, 23, 42, 0.5);
  border-color: rgba(6, 182, 212, 0.35);
}

.theme-space .optional-item {
  background: rgba(30, 41, 59, 0.8);
  border-color: rgba(255, 255, 255, 0.12);
}

.theme-space .optional-accent-purple { background: #a78bfa; }
.theme-space .optional-accent-blue { background: #22d3ee; }
.theme-space .optional-accent-orange { background: #fb923c; }

.theme-space .optional-item-icon-orange {
  background: rgba(249, 115, 22, 0.15);
  color: #fb923c;
}
.theme-space .optional-btn-orange {
  background: rgba(249, 115, 22, 0.15);
  color: #fb923c;
}

.theme-space .level-badge {
  background: linear-gradient(135deg, #06b6d4, #3b82f6);
  box-shadow: 0 4px 14px rgba(6, 182, 212, 0.35);
}

.theme-space .xp-bar {
  background: linear-gradient(90deg, #06b6d4, #3b82f6);
}

.theme-space .optional-icon-box {
  background: linear-gradient(135deg, #f97316, #ec4899);
}

.theme-space .journey-card {
  border-color: rgba(6, 182, 212, 0.3);
}

.theme-space .celebration-inner {
  background: linear-gradient(135deg, #0891b2, #6366f1, #8b5cf6);
}

/* ── Trail Theme ── */
.theme-trail .welcome-header {
  background: linear-gradient(135deg, #faf7f2, #f5f0e8);
}

.theme-trail .pad-margin-line {
  border-color: #d6a87c;
  opacity: 0.35;
}
.theme-trail .pad-ring {
  border-color: #b8860b;
}

.theme-trail .streak-pill {
  background: #f5f0e8;
  border-color: #e7e5e4;
}

.theme-trail .optional-card {
  background: #f5f0e8;
  border-color: #d6d3d1;
}

/* ── Road Theme ── */
.theme-road .welcome-header {
  background: linear-gradient(135deg, #f0fdfa, #e6f7f3);
}

.theme-road .pad-margin-line {
  border-color: #14b8a6;
  opacity: 0.35;
}

.theme-road .streak-pill {
  background: #e6f7f3;
  border-color: #ccfbf1;
}

.theme-road .optional-card {
  background: #e6f7f3;
  border-color: #99f6e4;
}

.theme-road .go-button-blue {
  background: #0d9488;
}
.theme-road .go-button-blue:hover {
  background: #0f766e;
}
</style>
